import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  GetScheuledTestsState,
  InitialState,
  UpdateAnswerPayload,
  GetAttemptedTestsState
} from './type'
import {
  getTestAverage,
  getScheuledTests,
  getAttemptedTests,
  testPaperForStudent,
  getStudentTestPaper,
  saveAnswer,
  submitTestPaper,
  updateTestStatus,
  answeredTestPaper,
  reportQuestion,
  addAssignment
} from './api'

// Define the initial state using that type
const initialState: InitialState = {
  average: null,
  isLoading: false,
  scheuledTests: [],
  practiceTests: [],
  attemptedTests: [],
  fullLengthTests: [],
  sectionWiseTests: [],
  chapterWiseTests: [],
  attemptedPracticeTests: [],
  testPaperDetails: null,
  testPaper: null,
  error: '',
  isSubmitting: false,
  result: null,
  selectedTestPaperId: '',
  isScheduledTestsLoading: false,
  isAttemptedTestsLoading: false,
  answeredTestResult: null,
  isReporting: false,
  reportedQuestion: '',
  attemptedChapterTests: [],
  status: ''
}

export const assessmentSlice = createSlice({
  name: 'test',
  initialState,
  reducers: {
    updateError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },
    updateStatus: (state, action: PayloadAction<any>) => {
      state.status = action.payload
    },
    updateMarkForReview: (
      state,
      action: PayloadAction<UpdateAnswerPayload>
    ) => {
      const updatedQns = state?.testPaper?.quesionList?.map((question) => {
        if (question.id === action?.payload?.id) {
          return {
            ...question,
            isMarkedForReview: action?.payload?.isMarkedForReview,
            isSelected: true
          }
        } else {
          return question
        }
      })
      console.log(updatedQns);

      state.testPaper = {
        sectionList: state.testPaper?.sectionList || [],
        quesionList: updatedQns?.length ? updatedQns : []
      }
    },
    updateAnswerForQuestion: (
      state,
      action: PayloadAction<UpdateAnswerPayload>
    ) => {
      const updatedQns = state?.testPaper?.quesionList?.map((question) => {
        if (question.id === action?.payload?.id) {
          return {
            ...question,
            answer: action?.payload?.selectedAnswer,
            isSelected: true
          }
        } else {
          return question
        }
      })

      state.testPaper = {
        sectionList: state.testPaper?.sectionList || [],
        quesionList: updatedQns?.length ? updatedQns : []
      }
    }
  },
  extraReducers: {
    [getTestAverage.pending.toString()]: (state) => {
      state.isLoading = true
      state.error = ''
      state.result = null
    },
    [getTestAverage.fulfilled.toString()]: (state, action) => {
      state.isLoading = false
      state.average = action?.payload
    },
    [getTestAverage.rejected.toString()]: (state) => {
      state.isLoading = false
      state.error = 'Unable to fetch the record. Please try again later.'
    },
    [getScheuledTests.pending.toString()]: (state) => {
      state.isScheduledTestsLoading = true
      state.error = ''
      state.result = null
    },
    [getScheuledTests.fulfilled.toString()]: (
      state,
      action: PayloadAction<GetScheuledTestsState>
    ) => {
      state.isScheduledTestsLoading = false
      state.scheuledTests = action?.payload?.scheuledTests
      state.practiceTests = action?.payload?.practiceTests
      state.fullLengthTests = action?.payload?.fullLengthTests
      state.sectionWiseTests = action?.payload?.sectionWiseTests
      state.chapterWiseTests = action?.payload?.chapterWiseTests
    },
    [getScheuledTests.rejected.toString()]: (state) => {
      state.isScheduledTestsLoading = false
      state.error =
        'Unable to get your scheduled tests. Please try again later.'
    },
    [getAttemptedTests.pending.toString()]: (state) => {
      state.isAttemptedTestsLoading = true
      state.error = ''
      state.result = null
    },
    [getAttemptedTests.fulfilled.toString()]: (
      state,
      action: PayloadAction<GetAttemptedTestsState>
    ) => {
      state.isAttemptedTestsLoading = false
      state.attemptedTests = action?.payload?.attemptedTests
      state.attemptedPracticeTests = action?.payload?.attemptedPracticeTests
      state.attemptedChapterTests = action?.payload?.attemptedchapterWiseTests
    },
    [getAttemptedTests.rejected.toString()]: (state) => {
      state.isAttemptedTestsLoading = false
      state.error =
        'Unable to get your attempted tests. Please try again later.'
    },
    [updateTestStatus.pending.toString()]: (state, action) => {
      state.selectedTestPaperId = action?.meta?.arg?.testPaperId
    },
    [testPaperForStudent.pending.toString()]: (state) => {
      state.isLoading = true
      state.error = ''
      state.result = null
    },
    [testPaperForStudent.fulfilled.toString()]: (state, action) => {
      state.isLoading = true
      state.testPaperDetails = action?.payload
    },
    [testPaperForStudent.rejected.toString()]: (state) => {
      state.isLoading = false
      state.error = 'Unable to fetch the record. Please try again later.'
    },
    [getStudentTestPaper.pending.toString()]: (state) => {
      state.isLoading = true
      state.error = ''
    },
    [getStudentTestPaper.fulfilled.toString()]: (state, action) => {
      state.isLoading = false
      state.testPaper = action?.payload
    },
    [getStudentTestPaper.rejected.toString()]: (state) => {
      state.isLoading = false
      state.error = 'Unable to fetch the test. Please try again later.'
    },
    [saveAnswer.pending.toString()]: (state) => {
      state.isSubmitting = true
      state.error = ''
    },
    [saveAnswer.fulfilled.toString()]: (state) => {
      state.isSubmitting = false
    },
    [saveAnswer.rejected.toString()]: (state) => {
      state.isSubmitting = false
      state.error = 'Unable to save your answer. Please try again.'
    },
    [submitTestPaper.pending.toString()]: (state) => {
      state.isSubmitting = true
      state.error = ''
    },
    [submitTestPaper.fulfilled.toString()]: (state, action) => {
      state.isSubmitting = false
      state.result = action?.payload
    },
    [submitTestPaper.rejected.toString()]: (state) => {
      state.isSubmitting = false
      state.error = 'Unable to submit your answers. Please try again.'
    },
    [answeredTestPaper.pending.toString()]: (state) => {
      state.isAttemptedTestsLoading = true
      state.error = ''
    },
    [answeredTestPaper.fulfilled.toString()]: (state, action) => {
      state.isAttemptedTestsLoading = false
      state.answeredTestResult = action?.payload?.answeredTestResult
      state.selectedTestPaperId = action?.payload?.selectedTestPaperId
    },
    [answeredTestPaper.rejected.toString()]: (state) => {
      state.isAttemptedTestsLoading = false
      state.error = 'Unable to fetch test results. Please try again.'
    },
    [reportQuestion.pending.toString()]: (state) => {
      state.isReporting = true
      state.error = ''
    },
    [reportQuestion.fulfilled.toString()]: (
      state,
      action: PayloadAction<string>
    ) => {
      state.isReporting = false
      state.reportedQuestion = action?.payload
    },
    [reportQuestion.rejected.toString()]: (state) => {
      state.isReporting = false
      state.error = 'Unable to report. Please try again later.'
    },
    [addAssignment.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [addAssignment.fulfilled.toString()]: (state) => {
      state.isLoading = false
    },
    [addAssignment.rejected.toString()]: (state) => {
      state.isLoading = false
      state.error = 'Unable to save assignment. Please try again later.'
    }
  }
})

export default assessmentSlice.reducer
